'use strict';

/**
 * RouteAnimator - Handles animated movement of markers along routes
 */

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

var RouteAnimator = function () {
    function RouteAnimator(mapService) {
        _classCallCheck(this, RouteAnimator);

        this.mapService = mapService;
        this.animatedMarkers = new Map(); // marker -> animation data
        this.isAnimating = false;
        this.animationId = null;

        // Bind methods for event handlers
        this.animate = this.animate.bind(this);
    }

    /**
     * Add a marker to be animated along a route
     * @param {Object} marker - The map marker to animate
     * @param {Array} routeWaypoints - Array of {lat, lng} waypoints defining the route
     * @param {Object} options - Animation options
     */


    _createClass(RouteAnimator, [{
        key: 'addAnimatedMarker',
        value: function addAnimatedMarker(marker, routeWaypoints) {
            var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

            var defaults = {
                speed: 30, // seconds to complete route
                direction: 'both', // 'forward', 'reverse', 'both'
                delay: 0, // seconds to delay start of animation
                pauseOnHover: true,
                loop: true
            };

            var settings = Object.assign({}, defaults, options);

            // Prepare route segments for smooth interpolation
            var segments = this.prepareRouteSegments(routeWaypoints);

            var animationData = {
                marker: marker,
                segments: segments,
                options: settings,
                currentPosition: 0, // 0-1 along entire route
                direction: 1, // 1 for forward, -1 for reverse
                isPaused: false,
                startTime: Date.now() + settings.delay * 1000, // Add delay in milliseconds
                totalDistance: this.calculateTotalDistance(segments),
                isDelayHidden: settings.delay > 0 // Track if marker is hidden due to delay
            };

            this.animatedMarkers.set(marker, animationData);

            // Note: Marker positioning and hiding for delayed animations is now handled
            // immediately in the map service addAnimatedPin methods for better timing

            // Set up hover pause functionality if enabled
            if (settings.pauseOnHover) {
                this.setupHoverPause(marker, animationData);
            }

            // Start animation if not already running
            if (!this.isAnimating) {
                this.startAnimation();
            }
        }

        /**
         * Remove animated marker
         * @param {Object} marker - The marker to stop animating
         */

    }, {
        key: 'removeAnimatedMarker',
        value: function removeAnimatedMarker(marker) {
            this.animatedMarkers.delete(marker);

            // Stop animation if no more markers
            if (this.animatedMarkers.size === 0) {
                this.stopAnimation();
            }
        }

        /**
         * Prepare route waypoints into segments with distance calculations
         * @param {Array} waypoints - Raw waypoints from route data
         * @returns {Array} Prepared segments with distance data
         */

    }, {
        key: 'prepareRouteSegments',
        value: function prepareRouteSegments(waypoints) {
            if (!waypoints || waypoints.length < 2) {
                return [];
            }

            var segments = [];
            var totalDistance = 0;

            for (var i = 0; i < waypoints.length - 1; i++) {
                var start = waypoints[i];
                var end = waypoints[i + 1];
                var distance = this.calculateDistance(start, end);

                segments.push({
                    start: { lat: start.lat, lng: start.lng },
                    end: { lat: end.lat, lng: end.lng },
                    distance: distance,
                    startDistance: totalDistance
                });

                totalDistance += distance;
            }

            // Add equal time ratios for consistent speed across all segments
            // Each segment gets equal time regardless of distance
            var numSegments = segments.length;
            segments.forEach(function (segment, index) {
                segment.startRatio = index / numSegments;
                segment.endRatio = (index + 1) / numSegments;
            });

            return segments;
        }

        /**
         * Calculate distance between two points (Haversine formula)
         * @param {Object} point1 - {lat, lng}
         * @param {Object} point2 - {lat, lng}
         * @returns {number} Distance in meters
         */

    }, {
        key: 'calculateDistance',
        value: function calculateDistance(point1, point2) {
            var R = 6371e3; // Earth's radius in meters
            var φ1 = point1.lat * Math.PI / 180;
            var φ2 = point2.lat * Math.PI / 180;
            var Δφ = (point2.lat - point1.lat) * Math.PI / 180;
            var Δλ = (point2.lng - point1.lng) * Math.PI / 180;

            var a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
            var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

            return R * c;
        }

        /**
         * Calculate total distance of all segments
         * @param {Array} segments - Route segments
         * @returns {number} Total distance in meters
         */

    }, {
        key: 'calculateTotalDistance',
        value: function calculateTotalDistance(segments) {
            return segments.reduce(function (total, segment) {
                return total + segment.distance;
            }, 0);
        }

        /**
         * Set up hover pause functionality for a marker
         * @param {Object} marker - The map marker
         * @param {Object} animationData - Animation data for this marker
         */

    }, {
        key: 'setupHoverPause',
        value: function setupHoverPause(marker, animationData) {}
        // This will be implemented when we integrate with the map services
        // Each map service (Leaflet/Mapbox) has different event systems


        /**
         * Start the animation loop
         */

    }, {
        key: 'startAnimation',
        value: function startAnimation() {
            if (this.isAnimating) return;

            this.isAnimating = true;
            this.animate();
        }

        /**
         * Stop the animation loop
         */

    }, {
        key: 'stopAnimation',
        value: function stopAnimation() {
            if (this.animationId) {
                cancelAnimationFrame(this.animationId);
                this.animationId = null;
            }
            this.isAnimating = false;
        }

        /**
         * Main animation loop
         */

    }, {
        key: 'animate',
        value: function animate() {
            var _this = this;

            if (!this.isAnimating) return;

            var currentTime = Date.now();

            // Update each animated marker
            this.animatedMarkers.forEach(function (animationData, marker) {
                if (animationData.isPaused) return;

                _this.updateMarkerPosition(animationData, currentTime);
            });

            // Continue animation
            this.animationId = requestAnimationFrame(this.animate);
        }

        /**
         * Update a single marker's position along its route
         * @param {Object} animationData - Animation data for the marker
         * @param {number} currentTime - Current timestamp
         */

    }, {
        key: 'updateMarkerPosition',
        value: function updateMarkerPosition(animationData, currentTime) {
            var options = animationData.options,
                segments = animationData.segments,
                startTime = animationData.startTime;


            if (!segments.length) return;

            // Check if animation should start yet (handle delay)
            if (currentTime < startTime) {
                return; // Animation hasn't started due to delay
            }

            // Show marker if it was hidden due to delay and animation is now starting
            if (animationData.isDelayHidden) {
                this.showMarker(animationData.marker);
                animationData.isDelayHidden = false;
            }

            // Calculate elapsed time and progress
            var elapsedTime = (currentTime - startTime) / 1000; // Convert to seconds

            // Use the speed setting as total route duration, but distribute time evenly across segments
            // This ensures consistent speed regardless of number of waypoints
            var speedMultiplier = options.direction === 'both' ? 2 : 1; // Take twice as long for bidirectional
            var cycleDuration = options.speed * speedMultiplier;

            var progress = void 0;

            if (options.direction === 'both') {
                // Ping-pong animation (forward then reverse)
                var cycleProgress = elapsedTime % cycleDuration / cycleDuration;
                progress = cycleProgress <= 0.5 ? cycleProgress * 2 // First half: 0 -> 1
                : 2 - cycleProgress * 2; // Second half: 1 -> 0
            } else {
                // Single direction animation
                progress = elapsedTime % options.speed / options.speed;
                if (options.direction === 'reverse') {
                    progress = 1 - progress;
                }
            }

            // Find the current segment
            var currentSegment = this.findSegmentAtProgress(segments, progress);
            if (!currentSegment) return;

            // Calculate position within the segment
            var segmentLength = currentSegment.endRatio - currentSegment.startRatio;
            var segmentProgress = segmentLength > 0 ? (progress - currentSegment.startRatio) / segmentLength : 0; // Handle zero-length segments

            var position = this.interpolatePosition(currentSegment.start, currentSegment.end, segmentProgress);

            // Update marker position using map service
            this.moveMarker(animationData.marker, position);
        }

        /**
         * Find which route segment contains the given progress value
         * @param {Array} segments - Route segments
         * @param {number} progress - Progress from 0-1 along route
         * @returns {Object|null} The segment containing this progress
         */

    }, {
        key: 'findSegmentAtProgress',
        value: function findSegmentAtProgress(segments, progress) {
            // Clamp progress to valid range
            progress = Math.max(0, Math.min(1, progress));

            // Handle edge cases
            if (progress === 0) return segments[0];
            if (progress === 1) return segments[segments.length - 1];

            // Find the segment that contains this progress
            for (var i = 0; i < segments.length; i++) {
                var segment = segments[i];
                if (progress >= segment.startRatio && progress < segment.endRatio) {
                    return segment;
                }
                // Handle the last segment (include endRatio)
                if (i === segments.length - 1 && progress <= segment.endRatio) {
                    return segment;
                }
            }

            // Fallback to last segment
            return segments[segments.length - 1];
        }

        /**
         * Interpolate position between two points
         * @param {Object} start - Start position {lat, lng}
         * @param {Object} end - End position {lat, lng}
         * @param {number} progress - Progress from 0-1 between points
         * @returns {Object} Interpolated position {lat, lng}
         */

    }, {
        key: 'interpolatePosition',
        value: function interpolatePosition(start, end, progress) {
            // Use linear interpolation for constant speed (no easing)
            var clampedProgress = Math.max(0, Math.min(1, progress));

            return {
                lat: start.lat + (end.lat - start.lat) * clampedProgress,
                lng: start.lng + (end.lng - start.lng) * clampedProgress
            };
        }

        /**
         * Easing function for smoother animation
         * @param {number} t - Progress from 0-1
         * @returns {number} Eased progress
         */

    }, {
        key: 'easeInOut',
        value: function easeInOut(t) {
            return t * t * (3 - 2 * t); // Smoothstep function
        }

        /**
         * Move marker to new position (implemented by map service integration)
         * @param {Object} marker - The map marker
         * @param {Object} position - New position {lat, lng}
         */

    }, {
        key: 'moveMarker',
        value: function moveMarker(marker, position) {}
        // This will be overridden by map service specific implementations


        /**
         * Hide marker (implemented by map service integration)
         * @param {Object} marker - The map marker to hide
         */

    }, {
        key: 'hideMarker',
        value: function hideMarker(marker) {}
        // This will be overridden by map service specific implementations


        /**
         * Show marker (implemented by map service integration)
         * @param {Object} marker - The map marker to show
         */

    }, {
        key: 'showMarker',
        value: function showMarker(marker) {}
        // This will be overridden by map service specific implementations


        /**
         * Pause animation for a specific marker
         * @param {Object} marker - The marker to pause
         */

    }, {
        key: 'pauseMarker',
        value: function pauseMarker(marker) {
            var animationData = this.animatedMarkers.get(marker);
            if (animationData) {
                animationData.isPaused = true;
                animationData.pauseStartTime = Date.now();
            }
        }

        /**
         * Resume animation for a specific marker
         * @param {Object} marker - The marker to resume
         */

    }, {
        key: 'resumeMarker',
        value: function resumeMarker(marker) {
            var animationData = this.animatedMarkers.get(marker);
            if (animationData) {
                animationData.isPaused = false;
                // Adjust start time to maintain current position instead of restarting
                var currentTime = Date.now();
                var pausedDuration = currentTime - (animationData.pauseStartTime || currentTime);
                animationData.startTime = (animationData.startTime || currentTime) + pausedDuration;
            }
        }
    }]);

    return RouteAnimator;
}();

module.exports = RouteAnimator;


//////////////////
// WEBPACK FOOTER
// ./assets/js/src/public/route-animator.js
// module id = 9
// module chunks = 0